/**
 * Portal hostnames — the FQDN an agency CNAMEs to us.
 *
 * Client-safe: only string work plus `isCanonicalHost` from `@repo/utils`, so
 * the settings form rejects a bad hostname before it reaches the domain
 * provider and starts costing API calls.
 */

import { isCanonicalHost } from "@repo/utils";

/** Suffixes we own. Attaching one would let an agency claim our own routing. */
const RESERVED_SUFFIXES = [".spyglasses.io", ".vercel.app"];

const MAX_HOSTNAME_LENGTH = 253;
const MAX_LABEL_LENGTH = 63;

/**
 * Reduce whatever the agency pasted to a bare lowercase hostname.
 *
 * Handles full URLs, a trailing path, a port, and the trailing dot of an
 * absolute DNS name.
 *
 * @example
 * normalizePortalHostname("  HTTPS://AI.Agency.com/portal ") // "ai.agency.com"
 * normalizePortalHostname("ai.agency.com:443.")              // "ai.agency.com"
 */
export function normalizePortalHostname(input: string): string {
	let host = input.trim().toLowerCase();
	if (!host) {
		return "";
	}

	host = host.replace(/^[a-z][a-z0-9+.-]*:\/\//, "");
	host = host.split(/[/?#]/)[0] ?? "";
	// Credentials in a pasted URL ("user@host") — keep the host half.
	const at = host.lastIndexOf("@");
	if (at !== -1) {
		host = host.slice(at + 1);
	}
	host = host.split(":")[0] ?? "";
	host = host.replace(/\.+$/, "");

	return host;
}

export type PortalHostnameValidation =
	| { ok: true }
	| { ok: false; reason: "invalid" | "canonical" | "reserved_suffix" };

function isValidFqdn(hostname: string): boolean {
	if (!hostname || hostname.length > MAX_HOSTNAME_LENGTH) {
		return false;
	}

	const labels = hostname.split(".");
	// A portal hostname is always a subdomain of something — a bare label is
	// either a typo or an internal name that could never CNAME to us.
	if (labels.length < 2) {
		return false;
	}

	for (const label of labels) {
		if (label.length === 0 || label.length > MAX_LABEL_LENGTH) {
			return false;
		}
		if (!/^[a-z0-9-]+$/.test(label)) {
			return false;
		}
		if (label.startsWith("-") || label.endsWith("-")) {
			return false;
		}
	}

	const tld = labels[labels.length - 1] as string;
	return /^[a-z]{2,}$/.test(tld);
}

/**
 * Can this hostname be attached as a portal domain?
 *
 * Checked in order: shape, then our own suffixes, then anything the app itself
 * answers on. Suffixes come before `isCanonicalHost` deliberately —
 * `*.vercel.app` satisfies both, and "reserved_suffix" is the reason an agency
 * can actually act on.
 *
 * @param env - Environment source, forwarded to `isCanonicalHost`
 */
export function validatePortalHostname(
	hostname: string,
	env: Record<string, string | undefined> = process.env,
): PortalHostnameValidation {
	if (!isValidFqdn(hostname)) {
		return { ok: false, reason: "invalid" };
	}

	if (RESERVED_SUFFIXES.some((suffix) => hostname.endsWith(suffix))) {
		return { ok: false, reason: "reserved_suffix" };
	}

	if (isCanonicalHost(hostname, env)) {
		return { ok: false, reason: "canonical" };
	}

	return { ok: true };
}
